import { useEffect, useState, useMemo, useCallback } from "react";
import { toast } from "sonner";
import Pagination from "./Pagination"; 
import TableSkeleton from '../../admin/TableSkeleton';
import '../style/eventable.css';

import { getCorporateActionStatus, validateEvent, processCorporateAction } from "../../service/api/eventService";
import { generateAccountHistoricReport } from "../../service/api/eventServiceReport";
import useSelectedDomain from "../../service/Hooks/useSelectedDomain";
import { useLoader } from "../../service/Hooks/useLoader";

import EventDetailsModal from '../EventDetailsModal ';
import EventTableRow from "./EventTableRow";
import NonCashConfirmationModal from "../../CorporateAction/NonCashConfirmationModal";
import CashDistributionModal from "../../CorporateAction/CashDistributionModal";

import { FaSearch, FaWifi, FaExclamationTriangle, FaRedo, FaSearch as FaMagnifier } from "react-icons/fa";

export function EventTables({ refresh }) {
  // 🔥 TOUS LES HOOKS EN HAUT - AUCUN IF AVANT
  const selectedDomain = useSelectedDomain();
  const { showLoader, hideLoader } = useLoader();
  
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOffline, setIsOffline] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);
  
  const [distributionModal, setDistributionModal] = useState({
    show: false, type: null, event: null, withTaxField: false
  });

  const itemsPerPage = 10;

  // 🔥 TOUS useMemo EN HAUT - JAMAIS conditionnels
  const filteredEvents = useMemo(() => {
    if (!searchQuery?.trim()) return events || [];
    const query = searchQuery.toLowerCase().trim();
    return (events || []).filter(event => 
      event?.type?.toLowerCase().includes(query) ||
      event?.targetMarket?.toLowerCase().includes(query) ||
      event?.securityName?.toLowerCase().includes(query)
    );
  }, [events, searchQuery]);

  const filteredEventsCount = filteredEvents.length;
  const currentItems = useMemo(() => {
    const offset = currentPage * itemsPerPage;
    return filteredEvents.slice(offset, offset + itemsPerPage);
  }, [filteredEvents, currentPage]);
  
  const pageCount = Math.ceil(filteredEventsCount / itemsPerPage);

  // Services
  const isConnectionError = useCallback((error) => {
    if (!error) return false;
    return !error.response || 
           error.code === 'ECONNABORTED' || 
           error.message?.includes('Network Error') ||
           error.message?.includes('Failed to fetch') ||
           error.message?.includes('connexion') ||
           error.message?.includes('network');
  }, []);

  const fetchEvents = useCallback(async (showLoading = true) => {
    if (!selectedDomain?.id) return;
    
    if (showLoading) setIsLoading(true);
    setError(null);
    
    try {
      const res = await getCorporateActionStatus(selectedDomain.id);
      const data = res.data || [];
      setEvents(data);
      setIsOffline(false);
      
      if (isOffline && data.length > 0) {
        toast.success("Connexion rétablie - Données synchronisées");
      }
    } catch (err) {
      console.error("Erreur fetchEvents:", err);
      
      if (isConnectionError(err)) {
        setIsOffline(true);
        setError("CONNEXION_LOST");
      } else {
        setIsOffline(false);
        setError(err.message || "Erreur lors du chargement");
        toast.error(err.message || "Erreur lors du chargement des données");
      }
    } finally {
      if (showLoading) setIsLoading(false);
    }
  }, [selectedDomain?.id, isOffline, isConnectionError]);

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(0); // Page 1 (index 0)
  }, []);

  const handleRetry = useCallback(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Effect principal
  useEffect(() => {
    fetchEvents();
    
    const handleOnline = () => {
      if (isOffline) {
        toast.info("Connexion rétablie");
        fetchEvents(false);
      }
    };
    
    const handleOffline = () => {
      setIsOffline(true);
      toast.warning("Vous êtes hors ligne");
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [selectedDomain, refresh, fetchEvents, isOffline]);

  // États calculés (fonctions normales - pas de hooks)
  const getNoResultsMessage = () => {
    if (searchQuery?.trim()) return `Aucun OST "${searchQuery}" trouvé`;
    return "Aucun événement OST disponible dans ce domaine";
  };

  const showInitialLoading = isLoading && (events?.length === 0) && !isOffline;
  const showNoResults = !isLoading && filteredEventsCount === 0 && !isOffline && !error;
  const showErrorState = error && !isOffline && !isLoading;
  const showOfflineState = isOffline;
  const showData = !isLoading && !isOffline && filteredEventsCount > 0;
  const noDomainSelected = !selectedDomain?.id;

  // HANDLERS
  const handleDistributeClick = useCallback((event, withTaxField = false) => {
    if (isOffline || !event) {
      toast.error("Action impossible en mode hors ligne");
      return;
    }

    let modalType = null;
    switch (event.type) {
      case 'Non Cash':
        modalType = 'non-cash';
        break;
      case 'Dividend to be paid':
      case 'Capital repayment':
        modalType = withTaxField ? 'cash-with-tax' : 'cash-without-tax';
        break;
      default:
        toast.error("Type d'événement non pris en charge pour la distribution.");
        return;
    }

    setDistributionModal({
      show: true,
      type: modalType,
      event: event,
      withTaxField: withTaxField
    });
  }, [isOffline]);

  const closeDistributionModal = useCallback(() => {
    setDistributionModal({ show: false, type: null, event: null, withTaxField: false });
  }, []);

  const handleConfirmDistribution = useCallback(async (impot = null) => {
    if (isOffline || !distributionModal.event) return;
    
    const { event } = distributionModal;
    showLoader({ message: "Distribution en cours...", indeterminate: true });
    
    try {
      const result = await processCorporateAction(event.id, impot);
      if (result) {
        toast.success(`Distribution ${event.type} effectuée avec succès !`);
        fetchEvents(false);
        closeDistributionModal();
      } else {
        toast.error("Erreur lors de la distribution.");
      }
    } catch (error) {
      toast.error(`Échec de la distribution: ${error.message || "Erreur inconnue"}`);
    } finally {
      hideLoader();
    }
  }, [isOffline, distributionModal.event, fetchEvents, closeDistributionModal]);

  const handleComplete = useCallback(async (id) => {
    if (isOffline || !selectedDomain?.id) {
      toast.error("Action impossible en mode hors ligne");
      return;
    }
    showLoader({ message: "Validation de l'événement en cours...", indeterminate: true });
    
    try {
      await validateEvent(id, selectedDomain.id);
      toast.success("L'événement est passé en Processed (Complété).");
      fetchEvents(false);
    } catch {
      toast.error("Erreur lors de la complétion de l'événement.");
    } finally {
      hideLoader();
    }
  }, [isOffline, selectedDomain?.id, fetchEvents]);

  const handleGenerateReport = useCallback(async (idCorporateAction) => {
    if (isOffline || !selectedDomain?.id) {
      toast.error("Génération impossible sans connexion");
      return;
    }
    showLoader({ message: "Génération du rapport en cours...", indeterminate: true });
    
    try {
      const result = await generateAccountHistoricReport(idCorporateAction, selectedDomain.id);
      toast.success("Rapport généré avec succès !");

      if (result?.url) {
        const link = document.createElement('a');
        link.href = result.url;
        link.setAttribute('download', result.fileName || "report.zip");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || "Erreur lors de la génération du rapport.";
      toast.error(errorMessage);
    } finally {
      hideLoader();
    }
  }, [isOffline, selectedDomain?.id]);

  const handleDetailsClick = useCallback((event) => {
    if (!event?.id) return;
    setSelectedEventId(event.id);
    setShowDetailsModal(true);
  }, []);

  const closeDetailsModal = useCallback(() => {
    setShowDetailsModal(false);
    setSelectedEventId(null);
  }, []);

  // 🔥 LOGIQUE CONDITIONNELLE DANS JSX - 0 hooks conditionnels
  if (noDomainSelected) {
    return (
      <div className="row mb-4">
        <div className="col-12 text-center py-8">
          <div className="alert alert-warning">
            <FaWifi className="fs-1 mb-3 text-warning" />
            <h5>Domaine non sélectionné</h5>
            <p className="mb-0">Veuillez sélectionner un domaine pour voir les événements OST</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="row mb-4 align-items-center">
      <div className="col-12">
        {showInitialLoading ? (
          <div className="py-5">
            <div className="d-flex justify-content-between mb-4 p-3 bg-white shadow-sm rounded">
              <h4 className="fw-bold text-uppercase fs-2" style={{ letterSpacing: "0.10em" }}>OST</h4>
              <div className="search-placeholder-skeleton" style={{ width: '300px', height: '40px' }} />
            </div>
            <TableSkeleton columns={11} rows={6} showHeader={true} />
          </div>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center mb-4 p-3 bg-white shadow-sm rounded">
              <h4 className="card-title fw-bold text-primary-emphasis text-center text-uppercase fs-2" 
                  style={{ letterSpacing: "0.10em" }}>
                OST
              </h4>
              <div className="position-relative search-container">
                <FaSearch className="search-icon" />
                <input
                  type="text"
                  className="search-input"
                  placeholder="Rechercher par type, marché ou titre..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  disabled={isOffline}
                />
              </div>
            </div>

            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h4 className="card-title fw-bold text-primary-emphasis text-center text-uppercase fs-2" 
                    style={{ letterSpacing: "0.10em" }}>
                  Liste des OST
                </h4>
                <div className="d-flex align-items-center gap-2">
                  {isOffline ? (
                    <span className="badge bg-warning-subtle text-warning">Hors ligne</span>
                  ) : (
                    <small className="text-muted">{filteredEventsCount} résultat(s)</small>
                  )}
                </div>
              </div>
              
              <div className="card-body">
                {showErrorState ? (
                  <div className="text-center py-8 p-4 border rounded-3 bg-danger-subtle">
                    <FaExclamationTriangle className="text-danger fs-1 mb-3" />
                    <h5 className="fw-bold mb-3 text-danger">{error}</h5>
                    <button className="btn btn-primary rounded-pill px-4" onClick={handleRetry}>
                      <FaRedo className="me-2" /> Réessayer
                    </button>
                  </div>
                ) : showNoResults ? (
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead className="table-light">
                        <tr>
                          <th>Type</th><th>Montant</th><th>Nombre titre</th><th>Capital (%)</th>
                          <th>Titre</th><th>Marché</th><th>Échéance</th><th>Exécution</th>
                          <th>Statut</th><th className="text-center">Distribuer</th><th className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td colSpan={11} className="text-center py-8">
                            <div className="text-muted">
                              <FaMagnifier className="fs-1 opacity-50 d-block mb-4 mx-auto" />
                              <h5 className="fw-bold mb-3">{getNoResultsMessage()}</h5>
                              <p className="mb-4">
                                {searchQuery ? "Essayez une recherche différente" : "Ce domaine n'a pas encore d'événements OST"}
                              </p>
                              {searchQuery && (
                                <button className="btn btn-outline-primary rounded-pill px-4" onClick={() => setSearchQuery("")}>
                                  Effacer
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ) : showOfflineState ? (
                  <div className="text-center py-8 p-4 border rounded-3 bg-warning-subtle">
                    <FaWifi className="text-warning fs-1 mb-3 opacity-75" />
                    <h5 className="fw-bold mb-3 text-warning">Mode hors ligne</h5>
                    <p className="mb-4 text-muted">
                      {events?.length > 0 
                        ? `📱 ${events.length} événement(s) consultables en cache` 
                        : "📭 Aucune donnée disponible hors ligne"
                      }
                    </p>
                    <button className="btn btn-outline-warning rounded-pill px-4" onClick={handleRetry}>
                      <FaRedo className="me-2" /> Reconnecter
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table table-sm table-striped table-hover">
                        <thead className="table-light">
                          <tr>
                            <th>Type</th><th>Montant</th><th>Nombre titre</th><th>Capital (%)</th>
                            <th>Titre</th><th>Marché</th><th>Échéance</th><th>Exécution</th>
                            <th>Statut</th><th className="text-center">Distribuer</th><th className="text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentItems.map((event) => (
                            <EventTableRow
                              key={event.id}
                              event={event}
                              onComplete={handleComplete}
                              onGenerateReport={handleGenerateReport}
                              onShowDetails={handleDetailsClick}
                              onDistributeClick={handleDistributeClick}
                              isOffline={isOffline}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {pageCount > 1 && (
                      <div className="border-top pt-4 mt-4">
                        <Pagination
                          currentPage={currentPage + 1}           
                          totalPages={pageCount}
                          onPageChange={(page) => {            
                            console.log(`Changement page: ${page}`); // Debug
                            setCurrentPage(page - 1);            
                          }}
                          ariaLabel="Pagination des événements OST"
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </>
        )}

        {/* MODALS */}
        {showDetailsModal && !isOffline && selectedEventId && (
          <EventDetailsModal 
            eventId={selectedEventId} 
            onClose={closeDetailsModal} 
          />
        )}

        <NonCashConfirmationModal
          show={distributionModal.show && distributionModal.type === 'non-cash'}
          onHide={closeDistributionModal}
          onConfirm={() => handleConfirmDistribution(null)}
          event={distributionModal.event}
          loading={false}
        />

        <CashDistributionModal
          show={distributionModal.show && distributionModal.type === 'cash-without-tax'}
          onHide={closeDistributionModal}
          onConfirm={() => handleConfirmDistribution(null)}
          event={distributionModal.event}
          loading={false}
          withTaxField={false}
        />

        <CashDistributionModal
          show={distributionModal.show && distributionModal.type === 'cash-with-tax'}
          onHide={closeDistributionModal}
          onConfirm={handleConfirmDistribution}
          event={distributionModal.event}
          loading={false}
          withTaxField={true}
        />
      </div>
    </div>
  );
}

export default EventTables;
